import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Layouts
import AdminLayout from './components/layouts/AdminLayout';
import LabelLayout from './components/layouts/LabelLayout';

// Pages
import Homepage from './pages/Homepage';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/admin/Dashboard';
import ContentPage from './pages/admin/ContentPage';
import RevenuePage from './pages/admin/RevenuePage';
import LabelsPage from './pages/admin/LabelsPage';
import InvoicesPage from './pages/admin/InvoicesPage';
import FounderDashboard from './pages/admin/FounderDashboard';
import UsersPage from './pages/admin/UsersPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import AssetsPage from './pages/admin/AssetsPage';
import FinanceDashboard from './pages/admin/FinanceDashboard';
import LabelDashboard from './pages/label/Dashboard';
import LabelRevenuePage from './pages/label/RevenuePage';
import LabelInvoicesPage from './pages/label/InvoicesPage';
import CreateInvoicePage from './pages/label/CreateInvoicePage';
import ReportsPage from './pages/shared/ReportsPage';
import ProfilePage from './pages/shared/ProfilePage';
import ClientSplash from './components/ClientSplash';

// Helper function to get dashboard route by role
const getDashboardByRole = (role) => {
    switch (role) {
        case 'admin':
            return '/admin/dashboard';
        case 'founder':
            return '/admin/founder';
        case 'finance':
            return '/admin/finance';
        case 'executive':
            return '/admin/founder';
        case 'label':
            return '/label/dashboard';
        default:
            return '/admin/dashboard';
    }
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading, isAuthenticated } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        // Determine login page based on allowed roles
        const isLabelRoute = allowedRoles && allowedRoles.includes('label');
        return <Navigate to={isLabelRoute ? '/label/login' : '/admin/login'} replace />;
    }

    // Check if user role is in allowed roles
    if (allowedRoles && !allowedRoles.includes(user?.role)) {
        // Redirect to appropriate dashboard based on user role
        return <Navigate to={getDashboardByRole(user?.role)} replace />;
    }

    return children;
};

// Landing redirect - Show Homepage for unauthenticated users, redirect to dashboard for authenticated
const LandingRedirect = () => {
    const { user, isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to={getDashboardByRole(user?.role)} replace />;
    }
    
    return <Homepage />;
};

// Admin index redirect based on role
const AdminIndexRedirect = () => {
    const { user } = useAuth();
    return <Navigate to={getDashboardByRole(user?.role)} replace />;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Landing */}
            <Route path="/" element={<LandingRedirect />} />

            {/* Auth Routes */}
            <Route path="/admin/login" element={<LoginPage />} />
            <Route path="/label/login" element={<LoginPage />} />

            {/* Admin Routes - Allow Founder, Admin, Finance */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute allowedRoles={['founder', 'admin', 'finance', 'executive']}>
                        <AdminLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<AdminIndexRedirect />} />
                <Route
                path="dashboard"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'founder', 'executive']}>
                        <AdminDashboard />
                    </ProtectedRoute>
                }
            />
                <Route
                path="founder"
                element={
                    <ProtectedRoute allowedRoles={['founder', 'executive']}>
                        <FounderDashboard />
                    </ProtectedRoute>
                }
            />
                <Route
                path="finance"
                element={
                    <ProtectedRoute allowedRoles={['founder', 'admin', 'finance']}>
                        <FinanceDashboard />
                    </ProtectedRoute>
                }
            />
                <Route
                path="content"
                element={
                    <ProtectedRoute allowedRoles={['founder', 'admin']}>
                        <ContentPage />
                    </ProtectedRoute>
                }
            />
                <Route
                path="revenue"
                element={
                    <ProtectedRoute allowedRoles={['founder', 'admin']}>
                        <RevenuePage />
                    </ProtectedRoute>
                }
            />
                <Route
                path="labels"
                element={
                    <ProtectedRoute allowedRoles={['founder', 'admin']}>
                        <LabelsPage />
                    </ProtectedRoute>
                }
            />
                <Route
                path="invoices"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'founder', 'finance', 'executive']}>
                        <InvoicesPage />
                    </ProtectedRoute>
                }
            />
                <Route
                path="users"
                element={
                    <ProtectedRoute allowedRoles={['founder', 'admin']}>
                        <UsersPage />
                    </ProtectedRoute>
                }
            />
                <Route
                path="assets"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'founder', 'executive']}>
                        <AssetsPage />
                    </ProtectedRoute>
                }
            />
                <Route
                path="audit-logs"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'founder', 'executive']}>
                        <AuditLogsPage />
                    </ProtectedRoute>
                }
            />
                <Route
                path="reports"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'founder', 'finance', 'executive']}>
                        <ReportsPage />
                    </ProtectedRoute>
                }
            />
                <Route
                path="profile"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'founder', 'finance', 'executive']}>
                        <ProfilePage />
                    </ProtectedRoute>
                }
            />
            </Route>

            {/* Label Routes */}
            <Route
                path="/label"
                element={
                    <ProtectedRoute allowedRoles={['label']}>
                        <LabelLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/label/dashboard" replace />} />
                <Route path="dashboard" element={<LabelDashboard />} />
                <Route path="revenue" element={<LabelRevenuePage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="invoices" element={<LabelInvoicesPage />} />
                <Route path="invoices/create" element={<CreateInvoicePage />} />
                <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <AppRoutes />
                    <ClientSplash />
                    <Toaster 
                        position="top-right" 
                        toastOptions={{
                            style: {
                                background: 'hsl(var(--card))',
                                color: 'hsl(var(--foreground))',
                                border: '1px solid hsl(var(--border))',
                            },
                        }}
                    />
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
